"use client";

import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface PillButtonProps {
  variant?: "primary" | "secondary" | "outline";
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  type?: "button" | "submit";
  disabled?: boolean;
  icon?: "arrow" | "github" | "bookmark" | "none";
  responsive?: boolean;
}

export function PillButton({
  variant = "primary",
  children,
  className,
  onClick,
  type,
  disabled = false,
  icon = "arrow",
  responsive = false,
}: PillButtonProps) {
  const Tag = type ? "button" : "span";

  return (
    <Tag
      {...(type ? { type, disabled } : {})}
      onClick={disabled ? undefined : onClick}
      className={cn(
        "group/btn inline-flex items-center gap-2.5 font-mono font-medium uppercase rounded-full transition-colors duration-200 cursor-pointer w-fit",
        responsive
          ? "text-xs sm:text-sm px-4 sm:px-6 py-2 sm:py-3"
          : "text-sm px-6 py-3",
        variant === "primary" && "bg-white text-[#09090a] hover:bg-white/90",
        variant === "secondary" &&
          "bg-transparent text-white border border-white/30 hover:border-white/60",
        variant === "outline" &&
          "bg-transparent text-[#0a0a0a] border border-[#0a0a0a]/30 hover:border-[#0a0a0a]/60",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {icon !== "none" && (
        <span
          className={cn(
            "w-5 h-5 rounded-full flex items-center justify-center transition-colors duration-200",
            variant === "primary" &&
              "bg-[#09090a] group-hover/btn:bg-[#7678E3]",
            variant === "secondary" && "bg-white",
            variant === "outline" && "bg-[#0a0a0a]"
          )}
        >
          {icon === "github" && (
            <svg
              className={cn(
                "w-3 h-3",
                variant === "primary" && "text-white",
                variant === "secondary" && "text-[#0a0a0a]",
                variant === "outline" && "text-white"
              )}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
            </svg>
          )}
          {icon === "bookmark" && (
            <svg
              className={cn(
                "w-2.5 h-2.5",
                variant === "primary" && "text-white",
                variant === "secondary" && "text-[#0a0a0a]",
                variant === "outline" && "text-white"
              )}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
            </svg>
          )}
          {icon === "arrow" && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={cn(
                "w-2.5 h-2.5",
                variant === "primary" && "text-white",
                variant === "secondary" && "text-[#0a0a0a]",
                variant === "outline" && "text-white"
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          )}
        </span>
      )}
      {children}
    </Tag>
  );
}
